import { asDate } from "@domain/model/Time";
import {
  asJobId,
  asClientId,
  asConstructionSiteId,
  asPersonId,
} from "@domain/model/Ids";

// DTOs
import type {
  JobDetailsResponse,
  JobDetailsContextDto,
  JobDetailsJobDto,
  JobDetailsStatsDto,
  JobBillingSummaryDto,
  BillingMissingItemDto,
  BillingWarningItemDto,
} from "../dto/JobDetailsDto";
import type { JobReportEventDto } from "../dto/JobReportDto";

// Domain
import type {
  JobDetails,
  JobDetailsContext,
  JobDetailsEvent,
  JobDetailsJob,
  JobDetailsStats,
  JobBillingSummary,
  BillingMissingItem,
  BillingWarningItem,
} from "@/domain/model/JobDetails";

import { jobReportEventFromDto } from "./JobReportMapper";

export const jobDetailsFromDto = (d: JobDetailsResponse): JobDetails => ({
  job: jobDetailsJobFromDto(d.job),
  context: jobDetailsContextFromDto(d.context),
  recentEvents: (d.recentEvents ?? []).map(jobDetailsEventFromDto),
  stats: jobDetailsStatsFromDto(d.stats),
  billing: jobBillingSummaryFromDto(d.billing),
});

export const jobDetailsJobFromDto = (d: JobDetailsJobDto): JobDetailsJob => ({
  id: asJobId(d.id),
  title: d.title,
  constructionSiteId: asConstructionSiteId(d.constructionSiteId),
  description: d.description ?? null,
  notes: d.notes ?? null,
  plannedStartAt: d.plannedStartAt ? asDate(d.plannedStartAt) : null,
  assignedPersonId: d.assignedPersonId ? asPersonId(d.assignedPersonId) : null,
  jobState: d.jobState,
  lastEventAt: d.lastEventAt ? asDate(d.lastEventAt) : null,
  version: d.version,
  createdAt: asDate(d.createdAt),
  updatedAt: asDate(d.updatedAt),
});

export const jobDetailsContextFromDto = (
  d: JobDetailsContextDto,
): JobDetailsContext => ({
  customer: {
    customerId: asClientId(d.customer.customerId),
    name: d.customer.name,
    address: d.customer.address ? { ...d.customer.address } : null,
  },
  site: {
    siteId: asConstructionSiteId(d.site.siteId),
    label: d.site.label,
    address: d.site.address ? { ...d.site.address } : null,
  },
});

export const jobDetailsStatsFromDto = (
  d: JobDetailsStatsDto,
): JobDetailsStats => ({ ...d });

/**
 * Reuse JobReportMapper’s event mapping to avoid duplicating:
 * - ID conversions (eventId/personId/attachmentId)
 * - date conversions (occurredAt + nested payload dates)
 * TODO
 * Since JobDetailsEvent is structurally identical to JobReportEvent
 * (same domain types reused), this is a straight pass-through.
 */
export const jobDetailsEventFromDto = (d: JobReportEventDto): JobDetailsEvent =>
  jobReportEventFromDto(d) as unknown as JobDetailsEvent;

export const jobBillingSummaryFromDto = (
  d: JobBillingSummaryDto,
): JobBillingSummary => ({
  time: { ...d.time }, // direct copy (numbers only)
  travel: { ...d.travel }, // direct copy (numbers/strings only; no dates/IDs)
  readiness: d.readiness, // string union passthrough
  missing: (d.missing ?? []).map(billingMissingItemFromDto),
  warnings: (d.warnings ?? []).map(billingWarningItemFromDto),
});

export const billingMissingItemFromDto = (
  d: BillingMissingItemDto,
): BillingMissingItem => ({
  code: d.code, // passthrough
});

export const billingWarningItemFromDto = (
  d: BillingWarningItemDto,
): BillingWarningItem => ({
  code: d.code, // passthrough
  severity: d.severity,
});
